import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent } from "@/components/core/card";
import DataTable from "@/components/core/data-table";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { useModal } from "@/components/core/modals/use-modal";
import { useContractInvoicesQuery } from "@/features/dashboard/api/queries/contract-invoice-query";
import {
	useDeleteSalesContractMutation,
	useExtendSalesContractMutation,
	useGroupedSalesContractsQuery,
	useSalesContractDashboardQuery,
	useSalesContractsQuery,
	useUpdateContractAsComplete,
} from "@/features/dashboard/api/queries/sales-contract-query";
import type { SalesContract, SalesContractFilters } from "@/features/dashboard/types/sales-contract";
import { CONTRACT_TYPE_LABELS, PROJECT_TYPE_LABELS } from "@/features/dashboard/types/sales-contract";
import type { ContractType } from "@/features/dashboard/types/sales-contract";
import { useAuth } from "@/hooks/use-auth";
import { exportSalesContractsToPDF, exportToCSV, exportToXLSX, prepareSalesContractDataForExport } from "@/utils/export-data";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import {
	IconBuilding,
	IconCalendar,
	IconCalendarPlus,
	IconCategory,
	IconCheck,
	IconChevronDown,
	IconChevronRight,
	IconCircleCheck,
	IconCrown,
	IconEdit,
	IconFileExport,
	IconFileInvoice,
	IconFileSpreadsheet,
	IconFileTypeCsv,
	IconFileTypePdf,
	IconPlus,
	IconSearch,
	IconTrash,
	IconUsers,
} from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { use } from "axios-request-throttle";
import { endOfYear, format, startOfMonth, startOfYear } from "date-fns";
import React, { useState } from "react";
import type { FC } from "react";
import { CollectionStatusModal } from "./collection-status-modal";
import { CreateContractModal } from "./create-contract-modal";
import { CreateInvoiceModal } from "./create-invoice-modal";
import { EditContractModal } from "./edit-contract-modal";
import { ExtendContractModal } from "./extend-contract-modal";
import { InvoiceRow } from "./invoice-row";
import { ContractsBreakdownModal, RecurringContractsModal } from "./recurring-contracts-modal";

export const SalesContractList: FC = () => {
	const { user, hasRole } = useAuth();
	const navigate = useNavigate();

	// Date filter state - separate form values from applied filter values
	const currentDate = new Date();
	const defaultDateFrom = format(startOfYear(currentDate), "yyyy-MM-dd");
	const defaultDateTo = format(endOfYear(currentDate), "yyyy-MM-dd");

	// Form input values (what user types)
	const [formDateFrom, setFormDateFrom] = useState(defaultDateFrom);
	const [formDateTo, setFormDateTo] = useState(defaultDateTo);

	// Applied filter values (what gets sent to API) - default to current year
	const [filterDateFrom, setFilterDateFrom] = useState(defaultDateFrom);
	const [filterDateTo, setFilterDateTo] = useState(defaultDateTo);

	// For salesperson users, filter to only their own contracts
	const salespersonId = hasRole(["salesperson"]) && !hasRole(["admin"]) ? user?.id : undefined;

	const [filters, setFilters] = useState<SalesContractFilters>({
		status: undefined,
		search: "",
		sort_by: "created_at",
		sort_order: "desc",
		per_page: 15,
		// Don't include date filters initially
		...(salespersonId && { salesperson_id: salespersonId }),
	});

	const [groupBy, setGroupBy] = useState<string | null>(null);

	const [currentPage, setCurrentPage] = useState(1);
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

	const modal = useModal();

	const { data, isLoading } = useSalesContractsQuery({ ...filters, page: currentPage });
	const { data: groupedData, isLoading: isGroupedLoading } = useGroupedSalesContractsQuery({
		...filters,
		group_by: groupBy,
		...(salespersonId && { salesperson_id: salespersonId }),
	});
	const { data: dashboardData } = useSalesContractDashboardQuery(
		filterDateFrom || undefined,
		filterDateTo || undefined,
		filters.status || undefined,
		salespersonId,
	);
	const deleteMutation = useDeleteSalesContractMutation();

	const updateContractAsCompleteMutation = useUpdateContractAsComplete();

	const handleSearch = (value: string) => {
		setFilters({
			...filters,
			search: value,
			...(salespersonId && { salesperson_id: salespersonId }),
		});
		setCurrentPage(1);
	};

	const handleStatusFilter = (value: string) => {
		setFilters({
			...filters,
			status: value === "all" ? undefined : (value as "active" | "expired" | "cancelled" | "completed"),
			...(salespersonId && { salesperson_id: salespersonId }),
		});
		setCurrentPage(1);
	};

	const handleProjectTypeFilter = (value: string) => {
		setFilters({
			...filters,
			project_type: value === "all" ? undefined : (value as "revlv_project" | "royalty_project"),
			...(salespersonId && { salesperson_id: salespersonId }),
		});
		setCurrentPage(1);
	};

	const handleContractTypeFilter = (value: string) => {
		setFilters({
			...filters,
			contract_type: value === "all" ? undefined : (value as ContractType),
			...(salespersonId && { salesperson_id: salespersonId }),
		});
		setCurrentPage(1);
	};

	const handleDelete = (contract: SalesContract) => {
		modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Delete Sales Contract",
			description: `Are you sure you want to delete the contract for "${contract.project_name}"? This action cannot be undone.`,
			onConfirm: async () => {
				await deleteMutation.mutateAsync(contract.id);
			},
		});
	};

	const handleComplete = (contract: SalesContract) => {
		modal.openModal({
			type: "confirm",
			variant: "green",
			title: "Mark Contract as Complete",
			description: `Update contract status for ${contract.project_name} as complete`,
			onConfirm: async () => {
				await updateContractAsCompleteMutation.mutateAsync({
					id: contract.id,
				});
			},
		});
	};

	const handleCreate = () => {
		modal.openModal({
			title: "Create Sales Contract",
			description: "Create a new sales contract with project details and recurring revenue information",
			size: "full",
			component: <CreateContractModal onSuccess={() => {}} />,
		});
	};

	const handleEdit = (contract: SalesContract) => {
		modal.openModal({
			title: "Edit Sales Contract",
			size: "full",
			description: "Update the sales contract details",
			component: <EditContractModal contract={contract} onSuccess={() => {}} />,
		});
	};

	const handleExtend = (contract: SalesContract) => {
		modal.openModal({
			title: "Extend Sales Contract",
			description: "Extend the end date of this sales contract",
			component: <ExtendContractModal contract={contract} onSuccess={() => {}} />,
		});
	};

	const handleAddInvoice = (contract: SalesContract) => {
		const InvoiceModal = modal.openModal({
			title: "Add Invoice",
			description: `Create a new invoice for ${contract.project_name}`,
			size: "lg",
			component: (
				<CreateInvoiceModal
					contract={contract}
					onSuccess={() => {
						modal.closeModal(InvoiceModal);
					}}
				/>
			),
		});
	};

	const handleMarkAsComplete = (contract: SalesContract) => {
		modal.openModal({
			title: "Update Collection Status",
			description: `Update collection status for ${contract.project_name}`,
			size: "lg",
			component: <CollectionStatusModal contract={contract} onSuccess={() => {}} />,
		});
	};

	const toggleRowExpansion = (contractId: string) => {
		const newExpandedRows = new Set(expandedRows);
		if (newExpandedRows.has(contractId)) {
			newExpandedRows.delete(contractId);
		} else {
			newExpandedRows.add(contractId);
		}
		setExpandedRows(newExpandedRows);
	};

	const handleGroupByChange = (newGroupBy: string | null) => {
		setGroupBy(newGroupBy);
		setCurrentPage(1);
	};

	const handleApplyFilter = () => {
		setFilterDateFrom(formDateFrom);
		setFilterDateTo(formDateTo);
		setFilters((prev) => ({
			...prev,
			start_date: formDateFrom,
			end_date: formDateTo,
			...(salespersonId && { salesperson_id: salespersonId }),
		}));
		setCurrentPage(1);
	};

	const handleResetToCurrentMonth = () => {
		const currentDate = new Date();
		const dateFrom = format(startOfMonth(currentDate), "yyyy-MM-dd");
		// To = today, not end-of-month — don't show future-dated period
		const dateTo = format(currentDate, "yyyy-MM-dd");

		setFormDateFrom(dateFrom);
		setFormDateTo(dateTo);
		setFilterDateFrom(dateFrom);
		setFilterDateTo(dateTo);
		setFilters((prev) => ({
			...prev,
			start_date: dateFrom,
			end_date: dateTo,
			...(salespersonId && { salesperson_id: salespersonId }),
		}));
		setCurrentPage(1);
	};

	const handleClearFilter = () => {
		const clearedFrom = "2010-01-01";
		const clearedTo = defaultDateTo;
		setFormDateFrom(clearedFrom);
		setFormDateTo(clearedTo);
		setFilterDateFrom(clearedFrom);
		setFilterDateTo(clearedTo);

		// Clear all filters except salesperson (if applicable)
		setFilters({
			status: undefined,
			search: "",
			start_date: formDateFrom,
			end_date: formDateTo,
			sort_by: "created_at",
			sort_order: "desc",
			per_page: 15,
			...(salespersonId && { salesperson_id: salespersonId }),
		});

		// Clear grouping
		setGroupBy(null);

		setCurrentPage(1);
	};

	const handleExport = async (format: "csv" | "xlsx" | "pdf") => {
		let exportContracts: Record<string, unknown>[] = [];

		if (groupBy && groupedData?.groups) {
			// Flatten grouped contracts for export
			exportContracts = groupedData.groups.flatMap((group) => group.contracts);
		} else if (data?.data) {
			exportContracts = data.data;
		}

		if (!exportContracts || exportContracts.length === 0) {
			return;
		}

		const { headers, rows } = prepareSalesContractDataForExport(exportContracts);
		const filename = `sales_contracts_${groupBy ? `grouped_by_${groupBy}_` : ""}${new Date().toISOString().split("T")[0]}`;

		switch (format) {
			case "csv":
				exportToCSV({ headers, rows, filename });
				break;
			case "xlsx":
				exportToXLSX({ headers, rows, filename });
				break;
			case "pdf":
				await exportSalesContractsToPDF({ headers, rows, filename });
				break;
		}
	};

	const getStatusBadge = (status: string) => {
		const colorMap: Record<string, string> = {
			active: "green",
			expired: "red",
			cancelled: "yellow",
			completed: "blue",
		};
		return <Badge color={colorMap[status] || "default"}>{status}</Badge>;
	};

	const columns = [
		{
			label: "",
			accessor: "expand",
			cell: (contract: SalesContract) => (
				<Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleRowExpansion(contract.id)}>
					{expandedRows.has(contract.id) ? <IconChevronDown className="h-4 w-4" /> : <IconChevronRight className="h-4 w-4" />}
				</Button>
			),
		},
		{
			label: "Project Name",
			accessor: "project_name",
			cell: (contract: SalesContract) => (
				<div className="max-w-[300px]">
					<a
						href={`/sales-contracts/${contract.id}`}
						onClick={(e) => {
							e.preventDefault();
							navigate({ to: "/sales-contracts/$contractId", params: { contractId: contract.id } });
						}}
						className="font-medium break-words whitespace-normal text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
					>
						{contract?.project_name}
					</a>
					{contract?.client_name && <p className="text-sm text-gray-500 break-words">{contract?.client_name}</p>}
					<div className="flex flex-wrap gap-1 mt-1">
						{contract?.project_category && (
							<span
								className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
									contract.project_category === "one-time"
										? "bg-blue-100 text-blue-800"
										: contract.project_category === "recurring"
											? "bg-green-100 text-green-800"
											: "bg-gray-100 text-gray-800"
								}`}
							>
								{contract.project_category === "one-time"
									? "One-time Contract"
									: contract.project_category === "recurring"
										? "Recurring Contract"
										: "Unknown Contract"}
							</span>
						)}
						{contract.project_type && (
							<span
								className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
									contract.project_type === "revlv_project" ? "bg-purple-100 text-purple-800" : "bg-orange-100 text-orange-800"
								}`}
							>
								{PROJECT_TYPE_LABELS[contract.project_type]}
							</span>
						)}
						{contract.contract_type && (
							<span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
								{CONTRACT_TYPE_LABELS[contract.contract_type]}
							</span>
						)}
					</div>
					{contract.tags && contract.tags.length > 0 && (
						<div className="flex flex-wrap gap-1 mt-1">
							{contract.tags.map((tag, index) => (
								<span key={index} className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
									{tag}
								</span>
							))}
						</div>
					)}
					{contract.royalty_amount && <p className="text-xs text-blue-600 mt-1">Royalty: ₱{formatNumber(contract.royalty_amount)}</p>}
				</div>
			),
		},
		{
			label: "Client",
			accessor: "client",
			cell: (contract: SalesContract) => (
				<div className="max-w-[200px] break-words whitespace-normal">
					<span className="font-medium">{contract?.project?.client?.name || contract?.project?.client?.company}</span>
				</div>
			),
		},
		{
			label: "Monthly Recurring",
			accessor: "monthly_recurring_sales",
			cell: (contract: SalesContract) => <span className="font-medium">₱{formatNumber(contract?.monthly_recurring_sales)}</span>,
		},
		{
			label: "Contract Value",
			accessor: "contract_amount",
			cell: (contract: SalesContract) => <span>₱{formatNumber(contract?.contract_amount)}</span>,
		},
		{
			label: "Start",
			accessor: "start_date",
			cell: (contract: SalesContract) => (
				<div className="text-sm">
					<p>{formatDate(contract?.start_date, "PP")}</p>
				</div>
			),
		},
		{
			label: "End",
			accessor: "effective_end_date",
			cell: (contract: SalesContract) => (
				<div className="text-sm">
					<p>{formatDate(contract?.end_date, "PP")}</p>
					{contract?.is_extended && (
						<Badge color="yellow" className="ml-1 text-xs">
							Extended
						</Badge>
					)}
				</div>
			),
		},
		{
			label: "Salesperson",
			accessor: "salesperson",
			cell: (contract: SalesContract) => (
				<div className="text-sm">
					<p>{contract?.salesperson?.name}</p>
					<p className="text-gray-500">{contract?.salesperson?.email}</p>
				</div>
			),
		},
		{
			label: "Status",
			accessor: "status",
			cell: (contract: SalesContract) => {
				return (
					<div className="flex items-center justify-between">
						{getStatusBadge(contract?.status)}

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
									<span className="sr-only">Open menu</span>
									<svg
										className="h-4 w-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
										role="img"
										aria-label="Menu options"
									>
										<title>Menu options</title>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
										/>
									</svg>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleEdit(contract)}>
									<IconEdit className="w-4 h-4 mr-2" />
									Edit
								</DropdownMenuItem>
								{/* <DropdownMenuItem 
									onClick={() => handleExtend(contract)}
									disabled={contract?.status !== "active"}
								>
									<IconCalendarPlus className="w-4 h-4 mr-2" />
									Extend
								</DropdownMenuItem> */}
								<DropdownMenuItem onClick={() => handleAddInvoice(contract)}>
									<IconFileInvoice className="w-4 h-4 mr-2" />
									Add Invoice
								</DropdownMenuItem>
								{/* <DropdownMenuItem onClick={() => handleMarkAsComplete(contract)}>
									<IconCircleCheck className="w-4 h-4 mr-2" />
									Mark as Complete
								</DropdownMenuItem> */}
								<DropdownMenuSeparator />
								{contract?.status === "expired" && (
									<DropdownMenuItem onClick={() => handleComplete(contract)}>
										<IconCheck className="w-4 h-4 mr-2" />
										Mark as Complete
									</DropdownMenuItem>
								)}
								<DropdownMenuItem onClick={() => handleDelete(contract)} className="text-red-600">
									<IconTrash className="w-4 h-4 mr-2" />
									Delete
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				);
			},
		},
	];

	// Calculate total invoice amount from current results (search-aware) - includes recurring + one-time
	const calculateTotalMonthlyRecurring = () => {
		if (groupBy && groupedData?.groups) {
			// Calculate from grouped data
			const total = groupedData.groups.reduce((total, group) => {
				return (
					total +
					group.contracts
						.filter((contract: Record<string, unknown>) => contract.project_type !== "royalty_project")
						.reduce((groupTotal: number, contract: Record<string, unknown>) => {
							const monthlyRecurring = contract.monthly_recurring_sales ? Number.parseFloat(contract.monthly_recurring_sales.toString()) : 0;
							const contractAmount = contract.contract_amount ? Number.parseFloat(contract.contract_amount.toString()) : 0;

							let value = 0;
							if (monthlyRecurring === 0 && contractAmount > 0) {
								value = contractAmount;
							} else {
								value = monthlyRecurring;
							}
							return groupTotal + (Number.isNaN(value) ? 0 : value);
						}, 0)
				);
			}, 0);
			return total;
		}
		if (data?.data) {
			// Calculate from current page data
			const total = data.data
				.filter((contract: Record<string, unknown>) => {
					const contractData = contract.data || contract;
					return contractData.project_type !== "royalty_project";
				})
				.reduce((total: number, contract: Record<string, unknown>) => {
					const contractData = contract.data || contract;
					const monthlyRecurring = contractData.monthly_recurring_sales ? Number.parseFloat(contractData.monthly_recurring_sales.toString()) : 0;
					const contractAmount = contractData.contract_amount ? Number.parseFloat(contractData.contract_amount.toString()) : 0;

					let value = 0;
					if (monthlyRecurring === 0 && contractAmount > 0) {
						value = contractAmount;
					} else {
						value = monthlyRecurring;
					}
					return total + (Number.isNaN(value) ? 0 : value);
				}, 0);
			return total;
		}
		return 0;
	};

	// Check if any filters are active (search, groupBy, or date filter applied)
	const hasActiveFilters = filters.search || groupBy || (filterDateFrom && filterDateTo);
	const hasActiveDateFilters = filterDateFrom && filterDateTo;
	const hasActiveSearchFilters = filters.search;

	const totalRoyaltyAmount = (() => {
		if (groupBy && groupedData?.total_royalty_amount !== undefined) {
			return groupedData.total_royalty_amount;
		}
		if (!hasActiveSearchFilters && dashboardData?.total_royalty_amount !== undefined) {
			return dashboardData.total_royalty_amount;
		}
		if (data?.total_royalty_amount !== undefined) {
			return data.total_royalty_amount;
		}
		return 0;
	})();

	const totalMonthlyRecurring = (() => {
		if (groupBy && groupedData?.total_monthly !== undefined) {
			return groupedData.total_monthly;
		}
		if (!hasActiveSearchFilters && dashboardData?.total_monthly !== undefined) {
			return dashboardData.total_monthly;
		}
		if (data?.total_monthly !== undefined) {
			return data.total_monthly;
		}
		return 0;
	})();

	const totalContractValue = (() => {
		if (groupBy && groupedData?.total_contract_value !== undefined) {
			return groupedData.total_contract_value;
		}
		if (!hasActiveSearchFilters && dashboardData?.total_contract_value !== undefined) {
			return dashboardData.total_contract_value;
		}
		if (data?.total_contract_value !== undefined) {
			return data.total_contract_value;
		}
		return 0;
	})();

	return (
		<div className="space-y-4">
			{/* Summary Cards at Top */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<button
					type="button"
					onClick={() =>
						modal.openModal({
							title: "Recurring Contracts",
							description: "Contracts contributing to Total Monthly Recurring (contract_amount ÷ duration in months)",
							size: "xl",
							component: (
								<RecurringContractsModal
									dateFrom={filterDateFrom || undefined}
									dateTo={filterDateTo || undefined}
									salespersonId={salespersonId ? String(salespersonId) : undefined}
									status={filters.status}
								/>
							),
						})
					}
					className="text-left bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
				>
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-sm font-medium text-blue-800">Total Monthly Recurring</h3>
							<p className="text-2xl font-bold text-blue-900">₱{formatNumber(totalMonthlyRecurring)}</p>
							<p className="text-xs text-blue-600 mt-1">Click to view recurring contracts</p>
						</div>
						<div className="text-blue-600">
							<IconCalendar className="w-8 h-8" />
						</div>
					</div>
				</button>
				<button
					type="button"
					onClick={() =>
						modal.openModal({
							title: "Total Contract Value",
							description: "Contracts contributing to Total Contract Value",
							size: "xl",
							component: (
								<ContractsBreakdownModal
									type="contract_value"
									dateFrom={filterDateFrom || undefined}
									dateTo={filterDateTo || undefined}
									salespersonId={salespersonId ? String(salespersonId) : undefined}
									status={filters.status}
								/>
							),
						})
					}
					className="text-left bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
				>
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-sm font-medium text-purple-800">Total Contract Value</h3>
							<p className="text-2xl font-bold text-purple-900">₱{formatNumber(totalContractValue)}</p>
							<p className="text-xs text-purple-600 mt-1">Click to view contracts</p>
						</div>
						<div className="text-purple-600">
							<IconFileExport className="w-8 h-8" />
						</div>
					</div>
				</button>
				<button
					type="button"
					onClick={() =>
						modal.openModal({
							title: "Total Royalty Project",
							description: "Royalty contracts contributing to the total",
							size: "xl",
							component: (
								<ContractsBreakdownModal
									type="royalty"
									dateFrom={filterDateFrom || undefined}
									dateTo={filterDateTo || undefined}
									salespersonId={salespersonId ? String(salespersonId) : undefined}
									status={filters.status}
								/>
							),
						})
					}
					className="text-left bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
				>
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-sm font-medium text-amber-800">Total Royalty Project</h3>
							<p className="text-2xl font-bold text-amber-900">₱{formatNumber(totalRoyaltyAmount)}</p>
							<p className="text-xs text-amber-600 mt-1">Click to view royalty contracts</p>
						</div>
						<div className="text-amber-600">
							<IconCrown className="w-8 h-8" />
						</div>
					</div>
				</button>
			</div>

			{/* Date Filter */}
			<Card>
				<CardContent className="pt-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
						<div className="flex items-center gap-2">
							<IconCalendar className="w-5 h-5 text-gray-500" />
							<span className="text-sm font-medium text-gray-700">Filter by Contract Start Date:</span>
						</div>
						<div className="flex items-center gap-4">
							<div className="flex items-center gap-2">
								<label htmlFor="date-from" className="text-sm text-gray-600">
									From:
								</label>
								<Input id="date-from" type="date" value={formDateFrom} onChange={(e) => setFormDateFrom(e.target.value)} className="w-auto" />
							</div>
							<div className="flex items-center gap-2">
								<label htmlFor="date-to" className="text-sm text-gray-600">
									To:
								</label>
								<Input id="date-to" type="date" value={formDateTo} onChange={(e) => setFormDateTo(e.target.value)} className="w-auto" />
							</div>
							<Button onClick={handleApplyFilter} size="sm" className="bg-blue-600 hover:bg-blue-700">
								Filter
							</Button>
							<Button variant="outline" onClick={handleResetToCurrentMonth} size="sm">
								This Month
							</Button>
							<Button variant="outline" onClick={handleClearFilter} size="sm" className="text-gray-600 hover:text-gray-800">
								Clear Filter
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Header and Filters */}
			<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
				<h1 className="text-2xl font-bold">Sales</h1>
				<div className="flex gap-2">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline">
								<IconCategory className="w-4 h-4 mr-2" />
								Group By
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => handleGroupByChange(null)}>
								<span className={`w-2 h-2 rounded-full mr-2 ${!groupBy ? "bg-blue-500" : "bg-gray-300"}`} />
								No Grouping
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={() => handleGroupByChange("category")}>
								<IconCategory className="w-4 h-4 mr-2" />
								<span className={`w-2 h-2 rounded-full mr-2 ${groupBy === "category" ? "bg-blue-500" : "bg-gray-300"}`} />
								By Project Category
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => handleGroupByChange("clientModel")}>
								<IconBuilding className="w-4 h-4 mr-2" />
								<span className={`w-2 h-2 rounded-full mr-2 ${groupBy === "clientModel" ? "bg-blue-500" : "bg-gray-300"}`} />
								By Client
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => handleGroupByChange("salesperson")}>
								<IconUsers className="w-4 h-4 mr-2" />
								<span className={`w-2 h-2 rounded-full mr-2 ${groupBy === "salesperson" ? "bg-blue-500" : "bg-gray-300"}`} />
								By Salesperson
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => handleGroupByChange("contract_type")}>
								<IconCategory className="w-4 h-4 mr-2" />
								<span className={`w-2 h-2 rounded-full mr-2 ${groupBy === "contract_type" ? "bg-blue-500" : "bg-gray-300"}`} />
								By Contract Type
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline">
								<IconFileExport className="w-4 h-4 mr-2" />
								Export
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onClick={() => handleExport("csv")}
								disabled={(!data?.data || data.data.length === 0) && (!groupedData?.groups || groupedData.groups.length === 0)}
							>
								<IconFileTypeCsv className="w-4 h-4 mr-2" />
								Export as CSV
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={() => handleExport("xlsx")}
								disabled={(!data?.data || data.data.length === 0) && (!groupedData?.groups || groupedData.groups.length === 0)}
							>
								<IconFileSpreadsheet className="w-4 h-4 mr-2" />
								Export as Excel
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={() => handleExport("pdf")}
								disabled={(!data?.data || data.data.length === 0) && (!groupedData?.groups || groupedData.groups.length === 0)}
							>
								<IconFileTypePdf className="w-4 h-4 mr-2" />
								Export as PDF
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
					<Button onClick={handleCreate}>
						<IconPlus className="w-4 h-4 mr-2" />
						New Contract
					</Button>
				</div>
			</div>

			<div className="flex flex-col sm:flex-row gap-4">
				<div className="w-full sm:w-[50%]">
					<div className="relative">
						<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
						<Input
							placeholder="Search by project name, client..."
							value={filters.search}
							onChange={(e) => handleSearch(e.target.value)}
							className="border py-2 pl-10"
						/>
					</div>
				</div>
				<div className="w-full sm:w-[20%]">
					<Select
						placeholder="Filter by status"
						options={[
							{ value: "all", label: "All Status" },
							{ value: "active", label: "Active" },
							{ value: "expired", label: "Expired" },
							{ value: "cancelled", label: "Cancelled" },
							{ value: "completed", label: "Completed" },
						]}
						defaultValue={filters.status || "all"}
						onValueChange={handleStatusFilter}
					/>
				</div>
				<div className="w-full sm:w-[20%]">
					<Select
						placeholder="Filter by project type"
						options={[
							{ value: "all", label: "All Types" },
							{ value: "revlv_project", label: "Revlv Project" },
							{ value: "royalty_project", label: "Royalty Project" },
						]}
						defaultValue={filters.project_type || "all"}
						onValueChange={handleProjectTypeFilter}
					/>
				</div>
				<div className="w-full sm:w-[20%]">
					<Select
						placeholder="Filter by contract type"
						options={[
							{ value: "all", label: "All Contract Types" },
							{ value: "hardware", label: "Hardware" },
							{ value: "software", label: "Software" },
							{ value: "hardware_software", label: "Hardware + Software" },
							{ value: "licenses", label: "Licenses" },
							{ value: "managed_services", label: "Managed Services" },
							{ value: "internet_connectivity", label: "Internet Connectivity" },
						]}
						defaultValue={filters.contract_type || "all"}
						onValueChange={handleContractTypeFilter}
					/>
				</div>
			</div>

			{/* Data Table */}
			<div className="bg-white rounded-lg shadow">
				{groupBy && groupedData ? (
					<div className="p-6">
						{isGroupedLoading ? (
							<div className="flex items-center justify-center py-8">
								<div className="text-center">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
									<p className="mt-2 text-sm text-gray-600">Loading grouped contracts...</p>
								</div>
							</div>
						) : (
							<div className="space-y-6">
								{groupedData.groups.map((group) => {
									// Note: This calculation still uses contract amounts, not actual invoice amounts
									// TODO: Update backend to provide per-group invoice totals
									const groupTotalMonthlyRecurring = group.contracts.reduce((acc: number, contract: Record<string, unknown>) => {
										const monthlyRecurring = Number.parseFloat(contract?.monthly_recurring_sales || 0);
										const contractAmount = Number.parseFloat(contract?.contract_amount || 0);

										// If monthly_recurring_sales is 0, it's a one-time project
										if (monthlyRecurring === 0 && contractAmount > 0) {
											return acc + contractAmount;
										}
										// Otherwise it's recurring, add monthly_recurring_sales
										return acc + monthlyRecurring;
									}, 0);
									const groupTotalContractValue = group.contracts.reduce(
										(acc: number, contract: Record<string, unknown>) => acc + Number.parseFloat(String(contract?.contract_amount) || "0"),
										0,
									);

									return (
										<div key={group.group} className="border rounded-lg">
											<div className="bg-gray-50 px-4 py-3 border-b">
												<h3 className="font-semibold text-gray-900">
													{group.group || "Unassigned"} ({group.count} contracts)
												</h3>
											</div>
											<div className="p-4">
												<ExpandableContractTable
													contracts={group.contracts}
													columns={columns}
													expandedRows={expandedRows}
													toggleRowExpansion={toggleRowExpansion}
													formDateFrom={filterDateFrom}
													formDateTo={filterDateTo}
												/>
											</div>
											<div className="bg-gray-50 px-4 py-3 border-t flex justify-end">
												<div className="flex gap-8 text-right">
													<div>
														<p className="text-sm text-gray-600">Total Invoice Amount</p>
														<p className="font-semibold text-gray-900">₱{formatNumber(groupTotalMonthlyRecurring)}</p>
													</div>
													<div>
														<p className="text-sm text-gray-600">Total Contract Value</p>
														<p className="font-semibold text-gray-900">₱{formatNumber(groupTotalContractValue)}</p>
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				) : (
					<>
						<ExpandableContractTable
							contracts={data?.data || []}
							columns={columns}
							expandedRows={expandedRows}
							toggleRowExpansion={toggleRowExpansion}
							isLoading={isLoading}
							formDateFrom={filterDateFrom}
							formDateTo={filterDateTo}
						/>

						{data?.meta && (
							<div className="px-6 py-4 border-t flex items-center justify-between">
								<div className="text-sm text-gray-600">
									Showing {data.meta.from} to {data.meta.to} of {data.meta.total} contracts
								</div>
								<div className="flex gap-2">
									<Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
										Previous
									</Button>
									<Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === data.meta.last_page}>
										Next
									</Button>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

// Custom expandable table component for contracts with invoice children
const ExpandableContractTable: FC<{
	contracts: SalesContract[];
	columns: Record<string, unknown>[];
	expandedRows: Set<string>;
	toggleRowExpansion: (contractId: string) => void;
	isLoading?: boolean;
	formDateFrom: string;
	formDateTo: string;
}> = ({ contracts, columns, expandedRows, toggleRowExpansion, isLoading, formDateFrom, formDateTo }) => {
	return (
		<div className="overflow-x-auto">
			<table className="w-full">
				<thead>
					<tr className="border-b-2 border-gray-200">
						{columns.map((column, index) => (
							<th key={index} className="text-left px-6 py-3 text-sm font-medium text-gray-900 uppercase tracking-wider">
								{column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{isLoading ? (
						<tr>
							<td colSpan={columns.length} className="px-6 py-8 text-center">
								<div className="flex items-center justify-center">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
									<span className="ml-2 text-sm text-gray-600">Loading contracts...</span>
								</div>
							</td>
						</tr>
					) : contracts.length === 0 ? (
						<tr>
							<td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500">
								No contracts found
							</td>
						</tr>
					) : (
						contracts.map((contract, rowIndex) => (
							<React.Fragment key={contract.id}>
								{/* Main contract row */}
								<tr className="border-b border-gray-200 hover:bg-gray-50">
									{columns.map((column, colIndex) => (
										<td key={colIndex} className="px-6 py-4 text-sm text-gray-900">
											{column.cell ? column.cell(contract) : contract[column.accessor as keyof SalesContract]}
										</td>
									))}
								</tr>

								{/* Expanded invoice rows */}
								{expandedRows.has(contract.id) && (
									<tr>
										<td colSpan={columns.length} className="px-6 py-0 bg-gray-50/30">
											<InvoiceSubTable contractId={contract.id} formDateFrom={formDateFrom} formDateTo={formDateTo} contract={contract} />
										</td>
									</tr>
								)}
							</React.Fragment>
						))
					)}
				</tbody>
			</table>
		</div>
	);
};

// Invoice sub-table component that loads invoices for a contract
const InvoiceSubTable: FC<{
	contractId: string;
	formDateFrom: string;
	formDateTo: string;
	contract: SalesContract;
}> = ({ contractId, formDateFrom, formDateTo, contract }) => {
	const { data: invoicesData, isLoading } = useContractInvoicesQuery(contractId, formDateFrom, formDateTo);
	const modal = useModal();

	const handleAddInvoiceFromSubTable = () => {
		modal.openModal({
			title: "Add Invoice",
			description: `Create a new invoice for ${contract.project_name}`,
			size: "lg",
			component: <CreateInvoiceModal contract={contract} onSuccess={() => {}} />,
		});
	};

	if (isLoading) {
		return (
			<div className="py-4 text-center">
				<div className="flex items-center justify-center">
					<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
					<span className="ml-2 text-sm text-gray-600">Loading invoices...</span>
				</div>
			</div>
		);
	}

	if (!invoicesData?.data || invoicesData.data.length === 0) {
		return <div className="py-4 text-center text-gray-500 text-sm">No invoices found for this contract</div>;
	}

	return (
		<div className="py-2">
			<div className="bg-blue-50 border-l-4 border-blue-200 rounded-r-md">
				<div className="px-4 py-2 border-b border-blue-200 bg-blue-100/50">
					<div className="flex items-center justify-between">
						<h4 className="text-sm font-medium text-blue-900 flex items-center">
							<IconFileInvoice className="w-4 h-4 mr-2" />
							Invoices ({invoicesData.data.length})
						</h4>
						<Button onClick={handleAddInvoiceFromSubTable} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
							<IconPlus className="w-3 h-3 mr-1" />
							Add Invoice
						</Button>
					</div>
				</div>
				<table className="w-full">
					<thead className="bg-blue-50/50">
						<tr>
							<th className="px-6 py-2 text-left text-xs font-medium text-blue-900 uppercase">Invoice #</th>
							<th className="px-6 py-2 text-left text-xs font-medium text-blue-900 uppercase">Description</th>
							<th className="px-6 py-2 text-left text-xs font-medium text-blue-900 uppercase">Amount</th>
							<th className="px-6 py-2 text-left text-xs font-medium text-blue-900 uppercase">Date / Due</th>
							<th className="px-6 py-2 text-left text-xs font-medium text-blue-900 uppercase">Date Collected</th>
							<th className="px-6 py-2 text-left text-xs font-medium text-blue-900 uppercase">Collection</th>
						</tr>
					</thead>
					<tbody>
						{invoicesData.data.map((invoice) => (
							<InvoiceRow key={invoice.id} invoice={invoice} />
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};
